import { parseGeoJson } from '@/utils/mapGeometry';

export const JSON_FIELD_TYPES = ['json', 'geojson'];

export const isJsonFieldType = (type) => JSON_FIELD_TYPES.includes(type);

export function hasJsonFieldValue(value) {
    if (value === null || typeof value === 'undefined') {
        return false;
    }

    if (typeof value === 'string') {
        return value.trim().length > 0;
    }

    return true;
}

export function parseJsonFieldValue(value) {
    if (!hasJsonFieldValue(value)) {
        return {
            empty: true,
            valid: false,
            value: null,
            error: null,
        };
    }

    if (typeof value === 'object') {
        return {
            empty: false,
            valid: true,
            value,
            error: null,
        };
    }

    if (typeof value !== 'string') {
        return {
            empty: false,
            valid: true,
            value,
            error: null,
        };
    }

    try {
        return {
            empty: false,
            valid: true,
            value: JSON.parse(value),
            error: null,
        };
    } catch (error) {
        return {
            empty: false,
            valid: false,
            value,
            error,
        };
    }
}

export function stringifyJsonFieldValue(value, pretty = true) {
    if (!hasJsonFieldValue(value)) {
        return '';
    }

    const parsed = parseJsonFieldValue(value);

    if (!parsed.valid) {
        return typeof value === 'string' ? value : String(value);
    }

    return JSON.stringify(parsed.value, null, pretty ? 2 : 0);
}

export function normalizeJsonFieldModelValue(value) {
    const parsed = parseJsonFieldValue(value);

    if (parsed.empty) {
        return null;
    }

    return parsed.valid ? parsed.value : value;
}

function pluralize(count, singular, plural = `${singular}i`) {
    return `${count} ${count === 1 ? singular : plural}`;
}

function summarizeObject(value) {
    const keys = Object.keys(value);
    return `JSON: oggetto, ${pluralize(keys.length, 'chiave', 'chiavi')}`;
}

function summarizeArray(value) {
    return `JSON: array, ${pluralize(value.length, 'elemento', 'elementi')}`;
}

function collectGeometryTypes(geojson) {
    if (!geojson || typeof geojson !== 'object') {
        return [];
    }

    if (geojson.type === 'FeatureCollection') {
        return (geojson.features ?? [])
            .map((feature) => feature?.geometry?.type)
            .filter(Boolean);
    }

    if (geojson.type === 'Feature') {
        return geojson.geometry?.type ? [geojson.geometry.type] : [];
    }

    if (geojson.type === 'GeometryCollection') {
        return (geojson.geometries ?? [])
            .map((geometry) => geometry?.type)
            .filter(Boolean);
    }

    return geojson.type ? [geojson.type] : [];
}

function summarizeGeometryTypes(types) {
    const uniqueTypes = [...new Set(types)].filter(Boolean);

    if (!uniqueTypes.length) {
        return '';
    }

    if (uniqueTypes.length <= 2) {
        return uniqueTypes.join(', ');
    }

    return `${uniqueTypes.slice(0, 2).join(', ')} +${uniqueTypes.length - 2}`;
}

export function summarizeJsonValue(value) {
    if (!hasJsonFieldValue(value)) {
        return '';
    }

    const parsed = parseJsonFieldValue(value);

    if (!parsed.valid) {
        const length = typeof value === 'string' ? value.length : String(value).length;
        return `JSON non valido, ${pluralize(length, 'carattere', 'caratteri')}`;
    }

    if (Array.isArray(parsed.value)) {
        return summarizeArray(parsed.value);
    }

    if (parsed.value && typeof parsed.value === 'object') {
        return summarizeObject(parsed.value);
    }

    return `JSON: ${typeof parsed.value}`;
}

export function summarizeGeoJsonValue(value) {
    if (!hasJsonFieldValue(value)) {
        return '';
    }

    const parsedJson = parseJsonFieldValue(value);

    if (!parsedJson.valid) {
        const length = typeof value === 'string' ? value.length : String(value).length;
        return `GeoJSON non valido, ${pluralize(length, 'carattere', 'caratteri')}`;
    }

    const geojson = parseGeoJson(parsedJson.value);

    if (!geojson) {
        return 'GeoJSON non riconosciuto';
    }

    const types = collectGeometryTypes(geojson);
    const typeSummary = summarizeGeometryTypes(types);

    if (geojson.type === 'FeatureCollection') {
        const featureCount = Array.isArray(geojson.features) ? geojson.features.length : 0;
        return ['GeoJSON: FeatureCollection', pluralize(featureCount, 'feature', 'feature'), typeSummary]
            .filter(Boolean)
            .join(', ');
    }

    if (geojson.type === 'Feature') {
        return ['GeoJSON: Feature', typeSummary].filter(Boolean).join(', ');
    }

    if (geojson.type === 'GeometryCollection') {
        const geometryCount = Array.isArray(geojson.geometries) ? geojson.geometries.length : 0;
        return ['GeoJSON: GeometryCollection', pluralize(geometryCount, 'geometria', 'geometrie')]
            .filter(Boolean)
            .join(', ');
    }

    return ['GeoJSON', typeSummary || geojson.type].filter(Boolean).join(': ');
}

export function getJsonFieldSummary(value, type = 'json') {
    return type === 'geojson' ? summarizeGeoJsonValue(value) : summarizeJsonValue(value);
}

export function jsonValueToDownloadText(value) {
    return stringifyJsonFieldValue(value, true);
}

export function getJsonFieldDownloadFileName(field = {}) {
    const extension = field.form_field_type_id === 'geojson' ? 'geojson' : 'json';
    const rawName = field.name || field.label || 'dato';
    const safeName = String(rawName)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/gi, '-')
        .replace(/^-+|-+$/g, '') || 'dato';

    return `${safeName}.${extension}`;
}